import type { FC, ReactNode } from "react";
import { Header, Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { skeletonStyle } from "../Skeletons/skeletons.js";
import { PageWidth, pageWidthClassNames } from "./pageWidth.js";

export interface PageHeaderProps {
  /** The page title. Optional so `isLoading` headers can omit it while the skeleton shows. */
  title?: ReactNode;
  /** Small muted line above the title (e.g. the parent entity's name). */
  eyebrow?: ReactNode;
  /** Right-aligned header controls (buttons, menus, toggles). */
  actions?: ReactNode;
  /** The page's tab strip (typically `Tabs`), rendered flush with the header's bottom border. */
  tabs?: ReactNode;
  /**
   * The page's filter bar (typically `FilterBar`), rendered full-bleed below
   * the title row and tab strip as the last pinned element of the header.
   */
  filterBar?: ReactNode;
  /**
   * The content column the title, actions, tabs, and filter bar are centered
   * in. Pass the same value to the page's body so the two stay aligned.
   * @default PageWidth.Full
   */
  width?: PageWidth;
  /** Replaces the title with a skeleton while the page's data loads. */
  isLoading?: boolean;
  className?: string;
}

/**
 * The header at the top of a page: an optional eyebrow, the page title with
 * its actions on the right, an optional tab strip, and an optional filter
 * bar. Sits full-bleed below the app's top bar and stays pinned while the
 * page content scrolls beneath it; the bottom border only renders around the
 * tab strip so tabless pages flow straight into their content.
 */
export const PageHeader: FC<PageHeaderProps> = (props) => {
  const {
    title,
    eyebrow,
    actions,
    tabs,
    filterBar,
    width = PageWidth.Full,
    isLoading = false,
    className,
  } = props;
  const headerContentClassName = pageWidthClassNames[width] || undefined;

  return (
    <Header
      className={twMerge(
        "sticky top-0 z-10 min-w-0 shrink-0 bg-card",
        className,
      )}
    >
      <div
        className={twMerge(
          "flex min-w-0 flex-col gap-1 px-4 pt-3 sm:px-6 sm:pt-4",
          headerContentClassName,
        )}
      >
        {eyebrow ? (
          <div className="text-xs leading-5 font-normal text-muted-foreground">
            {eyebrow}
          </div>
        ) : null}
        <div className="flex h-7 min-w-0 items-center justify-between gap-4">
          <Heading
            level={1}
            className="min-w-0 flex-1 truncate text-xl leading-7 font-semibold text-foreground"
          >
            {isLoading ? (
              <span className="block h-7 max-w-60 py-1">
                <span
                  className={skeletonStyle({ class: "block h-full w-full" })}
                />
              </span>
            ) : (
              title
            )}
          </Heading>

          {actions ? (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          ) : null}
        </div>
      </div>

      {tabs ? (
        // The border spans full bleed even when the strip is content-width.
        <div className="border-b border-border-light pt-3">
          <div className={twMerge("px-4 sm:px-6", headerContentClassName)}>
            {tabs}
          </div>
        </div>
      ) : null}

      <div className={headerContentClassName}>{filterBar}</div>
    </Header>
  );
};
